// Detección de contactos duplicados / similares dentro de un listado.
//
// Se considera duplicado cuando 2+ contactos comparten al menos uno de estos
// criterios normalizados:
//  - Teléfono: solo los últimos 10 dígitos (ignora prefijo internacional y
//    formato). Requiere al menos 7 dígitos reales para evitar coincidencias
//    triviales.
//  - Email: lowercase + trim, y solo si pasa la validación de email de negocio
//    (evita falsos positivos tipo logo@2x.png o placeholders comunes).
//  - Nombre: normalizado (sin acentos, lowercase, sin puntuación, espacios
//    colapsados). Requiere al menos 3 caracteres para no agrupar por "a" o "s".
//
// El algoritmo es O(n): construye tres índices por clave y luego marca
// cualquier contacto cuya clave tenga 2+ ocurrencias.

import { isValidBusinessEmail } from './email';

const digitsOnly = (s) => (s || '').toString().replace(/\D/g, '');

export function normalizeDupName(name) {
    if (!name || typeof name !== 'string') return '';
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // quita acentos
        .replace(/[^a-z0-9\s]/g, ' ')    // quita puntuación
        .replace(/\s+/g, ' ')
        .trim();
}

export function normalizeDupPhone(phone) {
    const digits = digitsOnly(phone);
    if (digits.length < 7) return '';
    // Usamos los últimos 10 dígitos para tolerar prefijos internacionales
    // distintos sobre el mismo número real.
    return digits.slice(-10);
}

export function normalizeDupEmail(email) {
    if (!email || typeof email !== 'string') return '';
    if (!isValidBusinessEmail(email)) return '';
    return email.toLowerCase().trim();
}

function getContactId(contact) {
    return contact.id || contact.placeId || null;
}

// Devuelve un Map<contactId, { reasons: Set<string>, matchIds: Set<string> }>.
// Un contacto está en el mapa si forma parte de al menos un grupo de >=2
// coincidentes. `reasons` indica por qué motivos se marcó como duplicado
// ('phone' | 'email' | 'name') y `matchIds` enumera los otros IDs con los que
// comparte alguna de esas claves.
export function findDuplicates(contacts) {
    const byPhone = new Map();
    const byEmail = new Map();
    const byName = new Map();

    if (!Array.isArray(contacts) || contacts.length === 0) {
        return new Map();
    }

    for (const contact of contacts) {
        const id = getContactId(contact);
        if (!id) continue;

        const phone = normalizeDupPhone(contact.phone);
        if (phone) {
            if (!byPhone.has(phone)) byPhone.set(phone, []);
            byPhone.get(phone).push(id);
        }

        const email = normalizeDupEmail(contact.email);
        if (email) {
            if (!byEmail.has(email)) byEmail.set(email, []);
            byEmail.get(email).push(id);
        }

        const name = normalizeDupName(contact.name);
        if (name && name.length >= 3) {
            if (!byName.has(name)) byName.set(name, []);
            byName.get(name).push(id);
        }
    }

    const duplicates = new Map();

    const markGroup = (ids, reason) => {
        if (ids.length < 2) return;
        for (const id of ids) {
            if (!duplicates.has(id)) {
                duplicates.set(id, { reasons: new Set(), matchIds: new Set() });
            }
            const entry = duplicates.get(id);
            entry.reasons.add(reason);
            for (const other of ids) {
                if (other !== id) entry.matchIds.add(other);
            }
        }
    };

    for (const ids of byPhone.values()) markGroup(ids, 'phone');
    for (const ids of byEmail.values()) markGroup(ids, 'email');
    for (const ids of byName.values()) markGroup(ids, 'name');

    return duplicates;
}

// Helper de UI: arma un texto legible con los motivos del duplicado.
const REASON_LABELS = {
    phone: 'teléfono',
    email: 'email',
    name: 'nombre',
};

export function describeDuplicateReasons(reasons) {
    if (!reasons || reasons.size === 0) return '';
    const labels = Array.from(reasons).map((r) => REASON_LABELS[r] || r);
    if (labels.length === 1) return `Duplicado por ${labels[0]}`;
    if (labels.length === 2) return `Duplicado por ${labels[0]} y ${labels[1]}`;
    return `Duplicado por ${labels.slice(0, -1).join(', ')} y ${labels.slice(-1)}`;
}
